import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowsInSimpleIcon,
  ArrowsOutSimpleIcon,
  PulseIcon,
  SidebarSimpleIcon,
} from '@phosphor-icons/react';

import { useAgentSession, useAgentSessionEvents } from '@/api-models/agent-sessions';
import { useRepositories } from '@/api-models/repositories';
import { useSessionPullRequest } from '@/api-models/sandbox-git';
import { useSpace, teamPath } from '@/contexts/space-context';
import { LocationBar } from '@/shared/shell/LocationBar';
import { Button, IconButton } from '@/ui/button/Button';
import { Spinner } from '@/ui/Spinner';

import { DEFAULT_PANEL_WIDTH, MIN_PANEL_WIDTH, MIN_THREAD_WIDTH, type PanelMode } from '../components/panel-layout';
import { ResizeHandle } from '../components/ResizeHandle';
import { SessionBudgetChip } from '../components/SessionBudget';
import { PullRequestChip } from '../components/PullRequestChip';
import { makeRepoLabel, sessionTitle } from '../components/session-controls';
import { SessionActionsMenu } from '../components/SessionActionsMenu';
import { AwaitingAnswerChip } from '../components/AwaitingAnswerChip';
import { sessionPhase } from '../components/session-status';
import { SessionHeaderFacts } from '../components/SessionHeaderFacts';
import { SessionStatusPill } from '../components/SessionStatusPill';
import { ThreadColumn } from '../components/ThreadColumn';
import { SidePanel } from '../components/SidePanel';

/**
 * A session's chat view.
 *
 * There is deliberately no loader branch for provisioning or paused sandboxes:
 * creating a session lands here immediately with the user's prompt already on
 * screen, and the sandbox lifecycle is reported on the line above the composer
 * instead of taking over the page. Nor is there auto-resume — with webhooks
 * wired up, a session going idle now surfaces within seconds, and resuming on
 * every such transition would spend the rest of the day fighting Daytona's
 * five-minute auto-stop. Resuming is the Resume button, or sending a message.
 */
export function AgentSessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { teamId } = useSpace();
  const navigate = useNavigate();
  const { session, isLoading, mutate } = useAgentSession(sessionId);
  const { repositories } = useRepositories(teamId);
  useAgentSessionEvents(sessionId);
  const repoLabel = makeRepoLabel(repositories);

  // Same SWR key the Review tab uses, so the two never disagree: opening, merging or closing a PR
  // there writes straight into this cache and the header chip moves with it. Skipped for plan
  // sessions — they have no branch, and the lookup 400s on one.
  const { pull } = useSessionPullRequest(
    session && session.mode !== 'plan' ? `/api/v1/agent-sessions/${session.id}` : null,
  );

  // Deliberately not persisted — the layout resets with the page.
  const [mode, setMode] = useState<PanelMode>('split');
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const splitRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const togglePanel = () => setMode((m) => (m === 'hidden' ? 'split' : 'hidden'));
  const toggleFull = () => setMode((m) => (m === 'full' ? 'split' : 'full'));

  // A plan session belongs on the plan page. Redirecting here rather than at each entry point
  // covers the session list, the sidebar, a bookmarked URL and anything added later — and this
  // page would otherwise show a session a sandbox-shaped UI it has no sandbox for.
  useEffect(() => {
    if (session?.mode === 'plan') {
      navigate(teamPath(teamId, `/sessions/${session.id}/plan`), { replace: true });
    }
  }, [session, teamId, navigate]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey)) return;
      // Leave the shell alone: Ctrl+B is the tmux prefix and Ctrl+\ is SIGQUIT,
      // so inside the terminal these belong to the process, not the layout.
      if (e.target instanceof Element && e.target.closest('.terminal-instance')) return;

      if (e.key === 'b') {
        e.preventDefault();
        togglePanel();
      } else if (e.key === '\\') {
        e.preventDefault();
        toggleFull();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const headerActions = session && (
    <div className="flex items-center gap-1">
      {/* Spend sits in the header rather than behind the menu: it moves on its
          own while the agent works, so it has to be visible without a click. */}
      <SessionBudgetChip session={session} />
      <SessionActionsMenu
        session={session}
        onChanged={() => void mutate()}
        onDeleted={() => navigate(teamPath(teamId, '/sessions'))}
      />
      <IconButton
        size={8}
        onClick={togglePanel}
        title={mode === 'hidden' ? 'Show panel  ⌘B' : 'Hide panel  ⌘B'}
        aria-label={mode === 'hidden' ? 'Show panel' : 'Hide panel'}
        aria-pressed={mode !== 'hidden'}
      >
        {/* Mirrored: Phosphor only draws this pointing at a left-hand sidebar,
            and the panel it toggles is on the right. */}
        <SidebarSimpleIcon size={15} className="-scale-x-100" />
      </IconButton>
      <IconButton
        size={8}
        onClick={toggleFull}
        title={mode === 'full' ? 'Restore split  ⌘\\' : 'Maximize panel  ⌘\\'}
        aria-label={mode === 'full' ? 'Restore split' : 'Maximize panel'}
        aria-pressed={mode === 'full'}
      >
        {mode === 'full' ? <ArrowsInSimpleIcon size={15} /> : <ArrowsOutSimpleIcon size={15} />}
      </IconButton>
    </div>
  );

  return (
    <div className="flex h-full flex-col leading-tight">
      <LocationBar action={headerActions ?? undefined}>
        <Button
          variant="ghost"
          shape="square"
          size={8}
          onClick={() => navigate(teamPath(teamId, '/sessions'))}
          aria-label="Back to sessions"
        >
          <ArrowLeftIcon size={15} />
        </Button>
        <PulseIcon size={16} weight="bold" className="shrink-0 text-brand-text" />
        {session ? (
          <>
            <SessionHeaderFacts
              title={sessionTitle(session)}
              repo={repoLabel(session.repository_id)}
              branch={session.branch}
            />
            {pull && <PullRequestChip pull={pull} />}
            <AwaitingAnswerChip session={session} />
            <SessionStatusPill phase={sessionPhase(session)} />
            {session.archived_at && (
              <span className="rounded-full bg-surface-3 px-2.5 py-1 text-2xs font-semibold text-fg-tertiary">
                Archived
              </span>
            )}
          </>
        ) : (
          <span className="text-sm font-semibold text-fg">Session</span>
        )}
      </LocationBar>

      {isLoading && !session ? (
        // Rare: `createAgentSession` primes this cache, so arriving from the
        // create flow paints the thread on the first frame.
        <div className="flex flex-1 items-center justify-center">
          <Spinner size={8} />
        </div>
      ) : !session ? (
        <div className="flex flex-1 items-center justify-center text-sm text-fg-tertiary">Session not found.</div>
      ) : (
        <div ref={splitRef} className="flex min-h-0 flex-1">
          <ThreadColumn session={session} hidden={mode === 'full'} onSessionChanged={() => void mutate()} />
          {mode === 'split' && (
            <ResizeHandle
              panelRef={panelRef}
              width={panelWidth}
              onWidth={setPanelWidth}
              min={MIN_PANEL_WIDTH}
              // Measured at grab time rather than tracked: the split's width only
              // matters while a drag is in flight.
              max={() => (splitRef.current?.clientWidth ?? 0) - MIN_THREAD_WIDTH}
              edge="start"
              onCollapse={() => setMode('hidden')}
              onReset={() => setPanelWidth(DEFAULT_PANEL_WIDTH)}
              label="Resize session panel"
            />
          )}
          {/* Keyed, so switching sessions remounts the whole panel.
              `sessions/:sessionId` renders one element for every session, so a
              param change re-renders this tree instead of replacing it, and every
              piece of state below is seeded on mount only: the selected file, the
              terminal's PTY session ids, the forwarded-port map, the desktop's
              preview URL. Without this they'd all carry over and describe the
              session you just left — pointing at a sandbox that isn't there any
              more. `useAgentSessionSocket` already does the equivalent for the
              thread by resetting the store on a `sessionId` change.

              The layout state above is deliberately *not* keyed: panel width and
              hide/maximize are the user's preference, not the session's. */}
          <SidePanel
            key={session.id}
            sessionId={session.id}
            ready={session.status === 'running'}
            mode={mode}
            width={panelWidth}
            panelRef={panelRef}
          />
        </div>
      )}
    </div>
  );
}
